import * as React from 'react';
import { useUserResolve } from '../../hooks/use-user-resolve';

/**
 * Displays a resolved user name for a given user ID.
 * If the value already contains '@' (is an email), displays it directly.
 * Otherwise, resolves the UUID via the user directory API.
 * Falls back to showing the raw value while loading or on error.
 */
export function ResolvedUserName({ userId }: { userId: string }): React.JSX.Element {
  const { resolvedUsers, resolve } = useUserResolve();

  React.useEffect(() => {
    // Only resolve if it looks like a UUID (not already an email or name)
    if (userId && !userId.includes('@') && !userId.includes(' ') && !(userId in resolvedUsers)) {
      resolve([userId]);
    }
  }, [userId, resolvedUsers, resolve]);

  // If it's already an email or has spaces (a name), show it directly
  if (userId.includes('@') || userId.includes(' ')) {
    return <>{userId}</>;
  }

  // Try to resolve
  const resolved = resolvedUsers[userId];
  if (resolved) {
    return <>{resolved.displayName}</>;
  }

  // Fallback: show the raw value (possibly truncated UUID)
  return <span className="text-muted-foreground">{userId.length > 20 ? `${userId.slice(0, 8)}...` : userId}</span>;
}
